import Vue from 'vue';
import { getStoreBuilder, BareActionContext } from 'vuex-typex';
import { AxiosError } from 'axios';
import authAxios from '@/state/auth-axios'; // Use this to make okta-auth'd axios requests
import rootStore from '@/state/store';

// === STATE
// Prepare initial state, store builder
type ISubData = any;
interface ISubStore extends IDictionary<any> { }
export const initialSubState: ISubStore = {
};

// Prepare store builder for use throughout
const storeBuilder = getStoreBuilder<IRootStore>();
const moduleBuilder = storeBuilder.module<ISubStore>(
	'sub',
	initialSubState,
);

// === GETTERS - Like computed data or used to get a part of the data or return it in a specific way and cache it
const SAMPLE_GETTER = moduleBuilder.read(
	(state: ISubStore) => (path: string) => {
		if (!state[path]) return [] as string[];
		return Object.keys(state[path].data);
	},
	'sampleGetter',
);

// === MUTATIONS - Synchronous work that mutates the data store. Should be quick
interface ISubDataMutationParams {
	path: string;
	data: ISubData;
}
const SET_SUBDATA_MUTATION = (
	state: ISubStore,
	data: ISubDataMutationParams,
) => {
	Vue.set(state, data.path, data.data); // For reactivity
};

// === ACTIONS - Asynchronous work that gets data, changes the data for storage, and handles errors.
type subStoreContext = BareActionContext<ISubStore, IRootStore>;
const GET_SUBDATA_ACTION = (
	{ }: subStoreContext,
	path: string,
): ISubDataPromise => {
	return fetchSubData({ path })
		.then((subdata) => {

			// If data doesn't already exist in store, add it
			if (!subStore.state[path]) {
				const subdataPayload: ISubDataMutationParams = {
					path,
					data: subdata,
				};

				// Commit document to store
				subStore.commitSetSubData(subdataPayload);
			}

			return subdata;
		})
		.catch((err: AxiosError) => {
			const msg: IMessage = {
				type: 'error',
				message: 'Error retrieving subdata',
			};
			rootStore.commitAddMessage(msg);
			throw err;
		});
};

// === SERVICES - Does work for request. Has ability to limit repeated requests or require requests to be a certain time apart.
type ISubDataPromise = Promise<ISubData>;
interface IFetchSubDataParams {
	path: string;
}
const fetchSubData = (args: IFetchSubDataParams): ISubDataPromise => {
	const {
		path,
	} = args;

	// prepare url for request
	// const config = rootStore.state.config as ISiteConfig;
	const url = `https://partner-portal/${path}`;

	// Determine query string params
	const params = {
	};

	// Create request and return promise for processed data
	return authAxios.get(url, {
			params,
		})
		// Success
		.then((response) => {
			const { data }: { data: ISubData } = response;
			return {
				data,
			};
		})
		// Error
		.catch((err: AxiosError) => {
			throw err;
		});
};

// === EXPORTS - Modules state or storage handling. Gets combined with the root store on compile or run.
// Prepare this module's state for export
const stateGetter = moduleBuilder.state();

// exported module interface
const subStore = {
	// state
	get state() { return stateGetter(); },

	// getters
	getSample(path: string) {
		return SAMPLE_GETTER()(path);
	},

	// mutations
	commitSetSubData: moduleBuilder.commit(SET_SUBDATA_MUTATION, 'SET_SUBDATA'),

	// action
	dispatchGetSubData: moduleBuilder.dispatch(
		GET_SUBDATA_ACTION,
		'getSubData',
	),
};

// Export default store object
export default subStore;
